import { useEffect, useState } from "react";
import _ from "lodash";
import {
    Autocomplete,
    Button,
    FormLayout,
    Heading,
    Select,
    Switch,
    Textarea,
} from "@/ui";
import { useForm, useValidator } from "@/hooks";
import {
    RESERVATION_MODE_OPTIONS,
    PAYMENTS_TYPES_OPTIONS,
} from "../../config/constant";
import checkoutStyles from "./CheckoutForm.module.scss";

const toSelectOptions = (options = []) =>
    options
        .filter(option => option?.value != null && option.value !== "")
        .map(option => ({
            value: String(option.value),
            label: option.label ?? String(option.value),
        }));

const CheckoutFormAddItem = ({
    store,
    handleCancel,
    handlerUpdatingOrCreate,
    isCreate,
    isLoadingUpdating,
    idSelected,
    form,
    stores,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const { inputs, inputChange, setInputs, selectChange, selectValue } =
        useForm();

    if (!_.get(inputs, "reservation_mode", null)) {
        if (!isCreate) {
            let itemUpdate = null;

            if (form === "Agreements") {
                itemUpdate = store.agreements.find(
                    (_, index) => index === idSelected,
                );
            }

            if (form === "Payments") {
                itemUpdate = store.payment_settings.find(
                    (_, index) => index === idSelected,
                );
            }

            setInputs(itemUpdate);
        }
    }

    useEffect(() => {
        setIsLoading(isLoadingUpdating);
    }, [isLoadingUpdating]);

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "reservation_mode",
                method: "isEmpty",
                validWhen: false,
                message: "This reservation mode is required.",
            },
            {
                field: "stores_provider_ids",
                method: "isEmpty",
                validWhen: false,
                message: "This stores provider is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const {
        validator: validatorPayments,
        isValid: isValidPayments,
        isSubmited: isSubmitedPayments,
    } = useValidator(
        [
            {
                field: "reservation_mode",
                method: "isEmpty",
                validWhen: false,
                message: "This reservation mode is required.",
            },
            {
                field: "payment_type",
                method: "isEmpty",
                validWhen: false,
                message: "This payment type is required.",
            },
            {
                field: "stores_provider_ids",
                method: "isEmpty",
                validWhen: false,
                message: "This stores provider is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const createOrUpdate = () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isSubmitedPayments) {
            isSubmitedPayments(true);
        }

        if (isValid() || isValidPayments()) {
            setIsLoading(true);
            let newList = [];

            if (form === "Agreements") {
                newList = _.cloneDeep(store.agreements || []);
            }

            if (form === "Payments") {
                newList = _.cloneDeep(store.payment_settings || []);
            }

            if (isCreate) {
                if (form === "Agreements") {
                    newList.push({
                        reservation_mode: inputs.reservation_mode,
                        content: inputs.content,
                        check_by_default: inputs.check_by_default,
                        required: inputs.required,
                        stores_provider_ids: inputs.stores_provider_ids,
                        __typename: "StoreAgreement",
                    });
                }

                if (form === "Payments") {
                    newList.push({
                        reservation_mode: inputs.reservation_mode,
                        payment_type: inputs.payment_type,
                        stores_provider_ids: inputs.stores_provider_ids,
                        __typename: "StorePaymentSettings",
                    });
                }
            } else {
                if (form === "Agreements") {
                    newList[idSelected] = {
                        ...inputs,
                        reservation_mode: inputs.reservation_mode,
                        content: inputs.content,
                        check_by_default: inputs.check_by_default,
                        required: inputs.required,
                        stores_provider_ids: inputs.stores_provider_ids,
                    };
                }
                if (form === "Payments") {
                    newList[idSelected] = {
                        ...inputs,
                        reservation_mode: inputs.reservation_mode,
                        payment_type: inputs.payment_type,
                        stores_provider_ids: inputs.stores_provider_ids,
                    };
                }
            }

            handlerUpdatingOrCreate(newList);
        }
    };

    const handleClose = () => {
        handleCancel();
    };

    // On change store logic
    const onChangeStore = values => {
        setInputs({ ...inputs, stores_provider_ids: values });
    };

    const getRenderValues = idsList => {
        if (!idsList) {
            return [];
        }
        const ids = Array.isArray(idsList) ? idsList : [idsList];
        return ids.map(String);
    };

    const reservationOptions = toSelectOptions(RESERVATION_MODE_OPTIONS);
    const paymentOptions = toSelectOptions(PAYMENTS_TYPES_OPTIONS);
    const providerOptions = toSelectOptions(stores);

    const selectedReservation = selectValue(
        inputs.reservation_mode,
        RESERVATION_MODE_OPTIONS,
    );
    const selectedPaymentType = selectValue(
        inputs.payment_type,
        PAYMENTS_TYPES_OPTIONS,
    );

    return (
        <div className={checkoutStyles.addItemPanel}>
            <Heading level={5}>
                {isCreate ? "Add " : "Update "}
                {`${form}`}
            </Heading>
            <FormLayout>
                {form === "Agreements" && (
                    <>
                        <div className={checkoutStyles.switchRow}>
                            <Switch
                                checked={inputs.check_by_default ? true : false}
                                label="Check By Default"
                                name="check_by_default"
                                onChange={inputChange}
                            />
                            <Switch
                                checked={inputs.required ? true : false}
                                label="Required"
                                name="required"
                                onChange={inputChange}
                            />
                        </div>
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Select
                                error={
                                    validator.reservation_mode.isInvalid
                                        ? validator.reservation_mode.message
                                        : undefined
                                }
                                fullWidth
                                label="Reservation Mode"
                                onValueChange={value => {
                                    const option =
                                        RESERVATION_MODE_OPTIONS.find(
                                            op => String(op.value) === value,
                                        );
                                    if (option) {
                                        selectChange(
                                            option,
                                            "reservation_mode",
                                        );
                                    }
                                }}
                                options={reservationOptions}
                                value={
                                    selectedReservation?.value != null
                                        ? String(selectedReservation.value)
                                        : undefined
                                }
                            />
                            <Autocomplete
                                error={
                                    validator.stores_provider_ids.isInvalid
                                        ? validator.stores_provider_ids.message
                                        : undefined
                                }
                                fullWidth
                                label="Stores Provider"
                                maxVisibleChips={1}
                                multiple
                                onChange={onChangeStore}
                                options={providerOptions}
                                value={getRenderValues(
                                    inputs.stores_provider_ids,
                                )}
                            />
                        </FormLayout.Grid>
                        <FormLayout.Grid columns={1} maxWidth="twoThirds">
                            <Textarea
                                fullWidth
                                label="Content"
                                name="content"
                                onChange={inputChange}
                                rows={6}
                                value={
                                    inputs.content !== "" ? inputs.content : ""
                                }
                            />
                        </FormLayout.Grid>
                    </>
                )}
                {form === "Payments" && (
                    <>
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Select
                                error={
                                    validatorPayments.reservation_mode.isInvalid
                                        ? validatorPayments.reservation_mode
                                              .message
                                        : undefined
                                }
                                fullWidth
                                label="Reservation Mode"
                                onValueChange={value => {
                                    const option =
                                        RESERVATION_MODE_OPTIONS.find(
                                            op => String(op.value) === value,
                                        );
                                    if (option) {
                                        selectChange(
                                            option,
                                            "reservation_mode",
                                        );
                                    }
                                }}
                                options={reservationOptions}
                                value={
                                    selectedReservation?.value != null
                                        ? String(selectedReservation.value)
                                        : undefined
                                }
                            />
                            <Select
                                error={
                                    validatorPayments.payment_type.isInvalid
                                        ? validatorPayments.payment_type.message
                                        : undefined
                                }
                                fullWidth
                                label="Payment Type"
                                onValueChange={value => {
                                    const option = PAYMENTS_TYPES_OPTIONS.find(
                                        op => String(op.value) === value,
                                    );
                                    if (option) {
                                        selectChange(option, "payment_type");
                                    }
                                }}
                                options={paymentOptions}
                                value={
                                    selectedPaymentType?.value != null
                                        ? String(selectedPaymentType.value)
                                        : undefined
                                }
                            />
                        </FormLayout.Grid>
                        <FormLayout.Grid columns={1} maxWidth="twoThirds">
                            <Autocomplete
                                error={
                                    validatorPayments.stores_provider_ids
                                        .isInvalid
                                        ? validatorPayments.stores_provider_ids
                                              .message
                                        : undefined
                                }
                                fullWidth
                                label="Stores Provider"
                                maxVisibleChips={2}
                                multiple
                                onChange={onChangeStore}
                                options={providerOptions}
                                value={getRenderValues(
                                    inputs.stores_provider_ids,
                                )}
                            />
                        </FormLayout.Grid>
                    </>
                )}
                <div className={checkoutStyles.addItemActions}>
                    <Button
                        disabled={isLoading}
                        onClick={() => {
                            handleClose();
                        }}
                        type="button"
                        variant="secondary"
                    >
                        Cancel
                    </Button>
                    <Button
                        loading={isLoading}
                        onClick={createOrUpdate}
                        type="button"
                    >
                        Save
                    </Button>
                </div>
            </FormLayout>
        </div>
    );
};

export default CheckoutFormAddItem;
